import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { PostCard } from "@/components/posts/PostCard";
import { EmptyState } from "@/components/shared/EmptyState";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Logo } from "@/components/shared/Logo";
import { POST_TYPE_LABEL, POST_TYPES } from "@/lib/labels";
import { Search, Filter, Plus, LogIn, MapPin, Zap, Clock, X, SearchCheck, Menu } from "lucide-react";
import { CustomerBottomNav } from "@/components/customer/CustomerBottomNav";
import { VoiceCaptureButton } from "@/components/customer/VoiceCaptureButton";
import { CategoryDrawer } from "@/components/customer/CategoryDrawer";
import { SmartSearchPartnerStrip } from "@/components/customer/SmartSearchPartnerStrip";
import { useAuth } from "@/contexts/AuthContext";
import { PublicBannerSlot } from "@/components/ads/PublicBannerSlot";
import { useCountdown, formatCountdown } from "@/hooks/useCountdown";
import { getLocalLocation } from "@/lib/location";

const PAGE_SIZE = 20;
const RADIUS_OPTIONS = [1, 3, 5, 10, 25];

/** Big countdown banner for /posts?shelf=flash_deals — earliest valid_to in the batch. */
function FlashDealsBanner({ posts }) {
    const earliest = useMemo(() => {
        const times = posts
            .filter((p) => p.valid_to)
            .map((p) => new Date(p.valid_to).getTime())
            .filter((t) => t > Date.now());
        return times.length ? new Date(Math.min(...times)).toISOString() : null;
    }, [posts]);
    const ctd = useCountdown(earliest);
    if (!earliest || ctd.expired) return null;
    return (
        <div
            data-testid="flash-deals-banner"
            className="mb-4 flex items-center justify-between rounded-2xl bg-gradient-to-r from-orange-500 to-red-500 px-4 py-3 text-white shadow"
        >
            <div className="flex items-center gap-3">
                <Zap size={22} />
                <div>
                    <div className="text-xs font-semibold uppercase tracking-wider opacity-90">Flash Deals</div>
                    <div className="text-lg font-extrabold">Ends in {formatCountdown(ctd)}</div>
                </div>
            </div>
            <div className="hidden text-right sm:block">
                <div className="text-[10px] uppercase tracking-wider opacity-80">Live</div>
                <div className="text-xs font-mono">{ctd.hours.toString().padStart(2, "0")}:{ctd.minutes.toString().padStart(2, "0")}:{ctd.seconds.toString().padStart(2, "0")}</div>
            </div>
        </div>
    );
}

/** Suggestion dropdown fetched from /search/suggest (debounced). */
function SearchSuggestions({ query, districtSlug, onPick }) {
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const nav = useNavigate();
    useEffect(() => {
        if (!query || query.trim().length < 2) { setItems([]); setOpen(false); return undefined; }
        const t = setTimeout(async () => {
            try {
                const params = new URLSearchParams({ q: query.trim(), limit: "8" });
                if (districtSlug) params.set("district_slug", districtSlug);
                const r = await api.get(`/search/suggest?${params.toString()}`);
                setItems(r.data || []);
                setOpen((r.data || []).length > 0);
            } catch { /* silent */ }
        }, 250);
        return () => clearTimeout(t);
    }, [query, districtSlug]);
    if (!open || !items.length) return null;
    const kindIcon = { recent: <Clock size={12} />, category: <span>🛍️</span>, business: <span>🏬</span>, product: <SearchCheck size={12} /> };
    return (
        <div
            data-testid="search-suggestions"
            className="absolute inset-x-0 top-full z-30 mt-1 max-h-80 overflow-y-auto rounded-xl border border-neutral-200 bg-white shadow-lg"
        >
            {items.map((it, i) => (
                <div
                    key={`${it.kind}-${it.term}-${i}`}
                    className="flex w-full items-center gap-2 border-b border-neutral-50 px-3 py-2 text-sm last:border-b-0 hover:bg-orange-50"
                >
                    <button
                        type="button"
                        onClick={() => onPick(it)}
                        data-testid={`search-suggestion-${i}`}
                        className="flex flex-1 items-center gap-2 text-left"
                    >
                        <span className="text-neutral-500">{kindIcon[it.kind] || "•"}</span>
                        <span className="flex-1 truncate">{it.term}</span>
                        <span className="text-[10px] uppercase tracking-wider text-neutral-400">{it.kind}</span>
                    </button>
                    {it.kind === "product" && (
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); nav(`/posts/compare?q=${encodeURIComponent(it.term)}${districtSlug ? `&district_slug=${encodeURIComponent(districtSlug)}` : ""}`); setOpen(false); }}
                            data-testid={`suggestion-compare-${i}`}
                            className="ml-2 shrink-0 rounded-full bg-orange-500 px-2 py-0.5 text-[10px] font-bold uppercase text-white hover:bg-orange-600"
                        >
                            Compare {it.count > 0 ? `${it.count} price${it.count === 1 ? "" : "s"}` : "prices"}
                        </button>
                    )}
                </div>
            ))}
        </div>
    );
}

export default function PostsPage({ mode = "app" }) {
    const { user } = useAuth();
    const isPublic = mode === "public";
    const navigate = useNavigate();
    const [sp, setSp] = useSearchParams();

    const [categories, setCategories] = useState([]);
    const [districts, setDistricts] = useState([]);

    const [filters, setFilters] = useState(() => ({
        type: sp.get("type") || "",
        category_slug: sp.get("category_slug") || "",
        district_slug: sp.get("district_slug") || "",
        search: sp.get("search") || "",
        sort: sp.get("sort") || "",
        shelf: sp.get("shelf") || "",
        festival: sp.get("festival") || "",
        new_store: sp.get("new_store") || "",
        nearby: sp.get("nearby") || "",
        lat: sp.get("lat") || "",
        lng: sp.get("lng") || "",
    }));

    const [posts, setPosts] = useState([]);
    const [page, setPage] = useState(0);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [radiusOpen, setRadiusOpen] = useState(false);
    const [compareMode, setCompareMode] = useState(false);
    const [categoryDrawerOpen, setCategoryDrawerOpen] = useState(false);
    const compareModeRef = useRef(false);
    useEffect(() => { compareModeRef.current = compareMode; }, [compareMode]);

    const sentinelRef = useRef(null);

    // Reference lists once
    useEffect(() => {
        Promise.all([api.get("/categories"), api.get("/districts")])
            .then(([c, d]) => { setCategories(c.data); setDistricts(d.data); })
            .catch(() => {});
    }, []);

    const buildParams = useCallback((pageIdx) => {
        const params = new URLSearchParams();
        if (filters.type) params.set("type", filters.type);
        if (filters.category_slug) params.set("category_slug", filters.category_slug);
        if (filters.district_slug) params.set("district_slug", filters.district_slug);
        if (filters.search) params.set("search", filters.search);
        if (filters.sort) params.set("sort", filters.sort);
        if (filters.shelf) params.set("shelf", filters.shelf);
        if (filters.festival) params.set("festival", filters.festival);
        if (filters.new_store) params.set("new_store", filters.new_store);
        if (filters.nearby) params.set("nearby", filters.nearby);
        if (filters.lat) params.set("lat", filters.lat);
        if (filters.lng) params.set("lng", filters.lng);
        params.set("limit", String(PAGE_SIZE));
        params.set("offset", String(pageIdx * PAGE_SIZE));
        return params;
    }, [filters]);

    // Sync visible URL to current filters (without paging params)
    useEffect(() => {
        const urlParams = buildParams(0);
        urlParams.delete("limit"); urlParams.delete("offset");
        setSp(urlParams, { replace: true });
    }, [buildParams, setSp]);

    // Fetch on filter change — reset paging
    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            setPage(0);
            setHasMore(true);
            try {
                const res = await api.get(`/posts?${buildParams(0).toString()}`);
                if (cancelled) return;
                setPosts(res.data);
                setHasMore((res.data || []).length >= PAGE_SIZE);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [buildParams]);

    // Log the search once when non-empty (fire & forget)
    useEffect(() => {
        if (!filters.search || filters.search.trim().length < 2) return;
        const t = setTimeout(() => {
            api.post("/search/log", { q: filters.search.trim(), district_slug: filters.district_slug || null }).catch(() => {});
        }, 800);
        return () => clearTimeout(t);
    }, [filters.search, filters.district_slug]);

    // Infinite scroll — IntersectionObserver on sentinel
    useEffect(() => {
        if (loading || !hasMore || loadingMore) return undefined;
        const el = sentinelRef.current;
        if (!el) return undefined;
        const obs = new IntersectionObserver(async (entries) => {
            if (!entries[0].isIntersecting) return;
            setLoadingMore(true);
            try {
                const next = page + 1;
                const res = await api.get(`/posts?${buildParams(next).toString()}`);
                const newItems = res.data || [];
                setPosts((prev) => [...prev, ...newItems]);
                setPage(next);
                setHasMore(newItems.length >= PAGE_SIZE);
            } finally {
                setLoadingMore(false);
            }
        }, { rootMargin: "400px" });
        obs.observe(el);
        return () => obs.disconnect();
    }, [loading, hasMore, loadingMore, page, buildParams]);

    const anyFilter = filters.type || filters.category_slug || filters.district_slug || filters.search;
    const nearbyActive = filters.nearby && filters.lat && filters.lng;

    // Customer location (for client-side distance badges when NOT using ?nearby)
    const localLoc = getLocalLocation();
    const userLat = user?.location_lat ?? (localLoc?.lat ?? null);
    const userLng = user?.location_lng ?? (localLoc?.lng ?? null);

    const setSearch = (val) => setFilters((f) => ({ ...f, search: val }));
    const pickSuggestion = (item) => {
        setSearch(item.term);
    };
    const chooseRadius = (km) => {
        setFilters((f) => ({ ...f, nearby: `${km}km` }));
        setRadiusOpen(false);
    };

    const body = (
        <>
            <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <div className="flex items-center gap-3">
                    <button
                        type="button"
                        onClick={() => setCategoryDrawerOpen(true)}
                        aria-label="Browse categories"
                        data-testid="home-hamburger"
                        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#FFF3E6] text-[var(--brand-orange)] transition hover:opacity-90"
                    >
                        <Menu size={18} />
                    </button>
                    <div>
                        <div className="font-heading text-sm font-semibold text-[var(--brand-orange)]">Explore</div>
                        <h1 className="section-title mt-1 text-3xl sm:text-4xl">
                            Discover offers &amp; events
                        </h1>
                        <p className="mt-1 text-sm text-[var(--text-muted)]">
                            Live posts across all 14 districts of Kerala.
                        </p>
                    </div>
                </div>
                {user && ["SUPER_ADMIN", "FRANCHISE_OWNER", "DISTRICT_MANAGER", "SUPERVISOR", "STAFF", "SHOP_OWNER"].includes(user.role) && (
                    <button type="button" onClick={() => navigate("/posts/new")} className="btn-primary" data-testid="posts-create-btn">
                        <Plus size={16} /> Create post
                    </button>
                )}
            </div>

            {/* Active category filter chip — visible when URL has `category_slug`.
             *  Mirrors the mobile Explore filter affordance so users know what's
             *  narrowing the feed and can clear it in one tap. */}
            {filters.category_slug && (
                <div className="mb-4" data-testid="posts-active-category-chip-wrap">
                    <span
                        className="inline-flex items-center gap-2 rounded-full border border-orange-500 bg-orange-50 px-3 py-1 text-sm font-semibold text-orange-700"
                        data-testid="posts-active-category-chip"
                    >
                        Category: {(categories.find((c) => c.slug === filters.category_slug)?.name) || filters.category_slug}
                        <button
                            type="button"
                            onClick={() => {
                                setFilters((f) => ({ ...f, category_slug: "" }));
                                const next = new URLSearchParams(sp);
                                next.delete("category_slug");
                                setSp(next, { replace: true });
                            }}
                            aria-label="Clear category filter"
                            data-testid="posts-clear-category-chip"
                            className="flex h-5 w-5 items-center justify-center rounded-full text-orange-600 hover:bg-orange-100"
                        >
                            <X size={12} />
                        </button>
                    </span>
                </div>
            )}

            {/* Nearby info strip */}
            {nearbyActive && (
                <div
                    data-testid="nearby-info-strip"
                    className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-2xl bg-[#FFF3E6] px-4 py-3 text-sm"
                >
                    <div className="flex items-center gap-2 text-neutral-800">
                        <MapPin size={16} className="text-orange-600" />
                        <span>Showing offers within <b>{filters.nearby}</b> of your location</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={() => setRadiusOpen(true)}
                            data-testid="change-radius-btn"
                            className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-orange-600 shadow-sm hover:text-orange-700"
                        >
                            Change radius
                        </button>
                        <button
                            type="button"
                            onClick={() => setFilters((f) => ({ ...f, nearby: "", lat: "", lng: "" }))}
                            data-testid="clear-nearby-btn"
                            className="inline-flex items-center gap-1 text-xs font-semibold text-neutral-500 hover:text-orange-600"
                        >
                            <X size={12} /> Clear
                        </button>
                    </div>
                </div>
            )}

            {/* Flash Deals top banner */}
            {filters.shelf === "flash_deals" && !loading && posts.length > 0 && (
                <FlashDealsBanner posts={posts} />
            )}

            {/* Filters */}
            <div className="mb-6 grid gap-3 rounded-2xl border border-neutral-200 bg-white p-4 sm:grid-cols-4">
                <div className="flex items-center gap-2 sm:col-span-1">
                    <div className="relative flex-1">
                        <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" size={16} />
                        <Input
                            placeholder={compareMode ? "Product name — press Enter to compare" : "Search title, business, product…"}
                            value={filters.search}
                            onChange={(e) => setSearch(e.target.value)}
                            onKeyDown={(e) => {
                                if (compareModeRef.current && e.key === "Enter" && e.currentTarget.value.trim().length > 1) {
                                    e.preventDefault();
                                    const q = e.currentTarget.value.trim();
                                    const params = new URLSearchParams({ q });
                                    if (filters.district_slug) params.set("district_slug", filters.district_slug);
                                    navigate(`/posts/compare?${params.toString()}`);
                                }
                            }}
                            className={`pl-9 pr-24 ${compareMode ? "ring-2 ring-orange-400" : ""}`}
                            data-testid="posts-search-input"
                            autoComplete="off"
                        />
                        <button
                            type="button"
                            onClick={() => setCompareMode((v) => !v)}
                            data-testid="posts-compare-toggle"
                            className={`absolute right-1 top-1/2 -translate-y-1/2 flex items-center gap-1 rounded-full px-2 py-1 text-[10px] font-bold uppercase tracking-wider transition ${compareMode ? "bg-orange-500 text-white" : "bg-neutral-100 text-neutral-600 hover:bg-orange-100 hover:text-orange-700"}`}
                        >
                            <SearchCheck size={12} /> Compare
                        </button>
                        <SearchSuggestions
                            query={filters.search}
                            districtSlug={filters.district_slug}
                            onPick={pickSuggestion}
                        />
                    </div>
                    <VoiceCaptureButton
                        surface="posts_search"
                        testId="search-mic-btn"
                        size="md"
                        onConfirm={(text) => setSearch(text)}
                    />
                </div>
                <Select
                    value={filters.type || undefined}
                    onValueChange={(v) => setFilters((f) => ({ ...f, type: v === "__all__" ? "" : v }))}
                >
                    <SelectTrigger data-testid="posts-filter-type">
                        <SelectValue placeholder="Type" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="__all__">All types</SelectItem>
                        {POST_TYPES.map((t) => (
                            <SelectItem key={t} value={t}>{POST_TYPE_LABEL[t]}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Select
                    value={filters.category_slug || undefined}
                    onValueChange={(v) => setFilters((f) => ({ ...f, category_slug: v === "__all__" ? "" : v }))}
                >
                    <SelectTrigger data-testid="posts-filter-category">
                        <SelectValue placeholder="Category" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="__all__">All categories</SelectItem>
                        {categories.map((c) => (
                            <SelectItem key={c.slug} value={c.slug}>{c.emoji} {c.name}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Select
                    value={filters.district_slug || undefined}
                    onValueChange={(v) => setFilters((f) => ({ ...f, district_slug: v === "__all__" ? "" : v }))}
                >
                    <SelectTrigger data-testid="posts-filter-district">
                        <SelectValue placeholder="District" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="__all__">All districts</SelectItem>
                        {districts.map((d) => (
                            <SelectItem key={d.slug} value={d.slug}>{d.name}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {/* Category chip row */}
            {categories.length > 0 && (
                <div className="mb-4 -mx-2 flex snap-x snap-mandatory items-center gap-2 overflow-x-auto px-2 pb-1" data-testid="posts-category-chip-row">
                    <button
                        type="button"
                        onClick={() => setFilters((f) => ({ ...f, category_slug: "" }))}
                        data-testid="posts-cat-chip-all"
                        className={`shrink-0 snap-start rounded-full border px-3 py-1.5 text-xs font-semibold transition ${!filters.category_slug ? "border-orange-500 bg-orange-500 text-white" : "border-neutral-300 bg-white text-neutral-700 hover:border-orange-400"}`}
                    >
                        All
                    </button>
                    {categories.map((c) => (
                        <button
                            key={c.slug}
                            type="button"
                            onClick={() => setFilters((f) => ({ ...f, category_slug: f.category_slug === c.slug ? "" : c.slug }))}
                            data-testid={`posts-cat-chip-${c.slug}`}
                            className={`shrink-0 snap-start rounded-full border px-3 py-1.5 text-xs font-semibold transition ${filters.category_slug === c.slug ? "border-orange-500 bg-orange-500 text-white" : "border-neutral-300 bg-white text-neutral-700 hover:border-orange-400"}`}
                        >
                            {c.emoji} {c.name}
                        </button>
                    ))}
                </div>
            )}

            {/* Sort chips */}
            <div className="mb-4 flex items-center gap-2" data-testid="posts-sort-chips">
                <span className="text-[11px] font-semibold uppercase text-neutral-500">Sort:</span>
                {[
                    { key: "", label: "Newest" },
                    { key: "discount_desc", label: "Highest Discount" },
                    { key: "price_asc", label: "Lowest Price" },
                    { key: "most_favorited", label: "Trending" },
                ].map((s) => (
                    <button
                        key={s.key || "newest"}
                        onClick={() => setFilters((f) => ({ ...f, sort: s.key }))}
                        data-testid={`posts-sort-chip-${s.key || "newest"}`}
                        className={`rounded-full border px-3 py-1 text-xs font-semibold transition ${filters.sort === s.key ? "border-neutral-900 bg-neutral-900 text-white" : "border-neutral-300 bg-white text-neutral-700 hover:border-orange-400"}`}
                    >
                        {s.label}
                    </button>
                ))}
            </div>

            {loading ? (
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                    {Array.from({ length: 8 }).map((_, i) => (
                        <Skeleton key={i} className="h-72 rounded-2xl" />
                    ))}
                </div>
            ) : posts.length ? (
                <>
                    <PublicBannerSlot
                        placement="HOME_TOP"
                        districtSlug={filters.district_slug}
                        className="mb-4 aspect-[16/5] w-full"
                    />
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="posts-grid">
                        {posts.map((p) => (
                            <PostCard key={p.id} post={p} userLat={userLat} userLng={userLng} />
                        ))}
                    </div>

                    {loadingMore && (
                        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="posts-loading-more">
                            {Array.from({ length: 4 }).map((_, i) => (
                                <Skeleton key={i} className="h-72 rounded-2xl" />
                            ))}
                        </div>
                    )}

                    {hasMore ? (
                        <div ref={sentinelRef} data-testid="posts-sentinel" className="h-8" />
                    ) : (
                        <div className="mt-8 text-center text-xs font-semibold uppercase tracking-wider text-neutral-400" data-testid="posts-end-marker">
                            You've reached the end
                        </div>
                    )}
                </>
            ) : (
                <EmptyState
                    title="No posts match your filters"
                    hint={anyFilter ? "Try relaxing the filters." : "Nothing has been published yet."}
                    icon={Filter}
                    action={
                        anyFilter ? (
                            <Button
                                variant="outline"
                                onClick={() => setFilters({ type: "", category_slug: "", district_slug: "", search: "", sort: "", shelf: "", festival: "", new_store: "", nearby: "", lat: "", lng: "" })}
                            >
                                Clear filters
                            </Button>
                        ) : null
                    }
                />
            )}

            {/* Radius bottom sheet */}
            <Sheet open={radiusOpen} onOpenChange={setRadiusOpen}>
                <SheetContent side="bottom" className="rounded-t-3xl bg-white" data-testid="radius-sheet">
                    <SheetHeader className="text-left">
                        <SheetTitle className="text-xl font-extrabold text-neutral-900">Change radius</SheetTitle>
                    </SheetHeader>
                    <div className="mt-4 grid grid-cols-5 gap-2">
                        {RADIUS_OPTIONS.map((km) => {
                            const active = filters.nearby === `${km}km`;
                            return (
                                <button
                                    key={km}
                                    onClick={() => chooseRadius(km)}
                                    data-testid={`radius-${km}`}
                                    className={`rounded-2xl border px-3 py-4 text-sm font-bold transition ${active ? "border-orange-500 bg-orange-500 text-white" : "border-neutral-200 bg-[#FFF3E6] text-neutral-800 hover:border-orange-400"}`}
                                >
                                    {km} km
                                </button>
                            );
                        })}
                    </div>
                </SheetContent>
            </Sheet>
            <CategoryDrawer open={categoryDrawerOpen} onOpenChange={setCategoryDrawerOpen} />
            <SmartSearchPartnerStrip query={filters.search} />
        </>
    );

    if (!isPublic) return (
        <div data-testid="posts-page" className="pb-24 lg:pb-6">
            {body}
            {user?.role === "CUSTOMER" && (
                <CustomerBottomNav homeHref={user?.district_slug ? `/home?district_slug=${user.district_slug}` : "/home"} />
            )}
        </div>
    );

    return (
        <div className="min-h-screen bg-neutral-50" data-testid="posts-page-public">
            <header className="border-b border-neutral-200 bg-white">
                <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 lg:px-8">
                    <Link to="/"><Logo size={32} /></Link>
                    <div className="flex items-center gap-2">
                        <Link to="/login">
                            <Button variant="outline" size="sm" data-testid="public-signin">
                                <LogIn size={14} className="mr-2" /> Sign in
                            </Button>
                        </Link>
                    </div>
                </div>
            </header>
            <main className="mx-auto max-w-7xl px-4 py-8 lg:px-8">{body}</main>
        </div>
    );
}
